import { clearToken, getToken } from './auth';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(
  path: string,
  options: RequestInit & { skipAuth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Content-Type', 'application/json');
  if (!options.skipAuth) {
    const token = getToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(path, { ...options, headers });

  if (res.status === 401 && !options.skipAuth) {
    clearToken();
    window.location.href = '/login';
    throw new ApiError(401, 'Unauthorized');
  }

  if (!res.ok) {
    let msg = `API error ${res.status}`;
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, msg);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// === Types ===
export interface Friend {
  line_user_id: string;
  display_name: string;
  picture_url: string | null;
  followed_at: string;
  last_message_at: string | null;
  last_read_at: string | null;
  is_bookmarked: boolean;
  is_unread: boolean;
  memo: string | null;
  status_id: number | null;
  status_name: string | null;
  status_color: string | null;
  inflow_route_id: number | null;
  inflow_route_name: string | null;
  inflow_route_channel: string | null;
  last_message_preview: string | null;
  last_message_direction: 'inbound' | 'outbound' | null;
  tags: { id: number; name: string }[];
}

export interface Message {
  id: number;
  line_user_id: string;
  direction: 'inbound' | 'outbound';
  message_type: string;
  content: string;
  sent_at: string;
  sent_by: number | null;
}

export interface Status {
  id: number;
  name: string;
  color: string;
  order_index: number;
  auto_tag_id: number | null;
  auto_tag_name: string | null;
  usage_count: number;
}

export interface Tag {
  id: number;
  name: string;
  created_at: string;
  user_count: number;
  route_count: number;
}

export interface InflowRoute {
  id: number;
  name: string;
  description: string | null;
  channel: string;
  url_slug: string;
  is_active: boolean;
  created_at: string;
  user_count: number;
  tags: { id: number; name: string }[];
}

// === Auth ===
export const auth = {
  login: (password: string) =>
    request<{ token: string }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ password }),
      skipAuth: true,
    }),
};

// === Friends ===
export interface FriendListParams {
  q?: string;
  status_id?: string;
  tag_id?: string;
  bookmarked?: boolean;
  unread_only?: boolean;
}

export const friends = {
  list: (params: FriendListParams = {}) => {
    const sp = new URLSearchParams();
    if (params.q) sp.set('q', params.q);
    if (params.status_id) sp.set('status_id', params.status_id);
    if (params.tag_id) sp.set('tag_id', params.tag_id);
    if (params.bookmarked) sp.set('bookmarked', 'true');
    if (params.unread_only) sp.set('unread_only', 'true');
    const qs = sp.toString();
    return request<{ friends: Friend[]; total: number }>(
      `/api/friends${qs ? `?${qs}` : ''}`,
    );
  },
  get: (id: string) => request<Friend>(`/api/friends/${id}`),
  update: (
    id: string,
    body: Partial<{
      status_id: number | null;
      is_bookmarked: boolean;
      memo: string;
      inflow_route_id: number | null;
    }>,
  ) =>
    request<{ updated: boolean }>(`/api/friends/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  markRead: (id: string) =>
    request<{ updated: boolean }>(`/api/friends/${id}/read`, { method: 'POST' }),
  markUnread: (id: string) =>
    request<{ updated: boolean }>(`/api/friends/${id}/read`, { method: 'DELETE' }),
  addTag: (id: string, body: { tag_id?: number; tag_name?: string }) =>
    request<{ tag_id: number }>(`/api/friends/${id}/tags`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeTag: (id: string, tagId: number) =>
    request<{ deleted: boolean }>(
      `/api/friends/${id}/tags?tag_id=${tagId}`,
      { method: 'DELETE' },
    ),
};

// === Messages ===
export const messages = {
  list: (friendId: string) =>
    request<{ messages: Message[] }>(`/api/friends/${friendId}/messages`),
  send: (friendId: string, content: string) =>
    request<Message>(`/api/friends/${friendId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),
};

// === Statuses ===
export const statuses = {
  list: () => request<{ statuses: Status[] }>('/api/statuses'),
  create: (body: {
    name: string;
    color: string;
    order_index?: number;
    auto_tag_id?: number | null;
  }) =>
    request<{ id: number }>('/api/statuses', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (
    id: number,
    body: Partial<{
      name: string;
      color: string;
      order_index: number;
      auto_tag_id: number | null;
    }>,
  ) =>
    request<{ updated: boolean }>(`/api/statuses/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    request<void>(`/api/statuses/${id}`, { method: 'DELETE' }),
};

// === Tags ===
export const tags = {
  list: () => request<{ tags: Tag[] }>('/api/tags'),
  create: (name: string) =>
    request<{ id: number }>('/api/tags', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),
  update: (id: number, name: string) =>
    request<{ updated: boolean }>(`/api/tags/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    }),
  remove: (id: number) => request<void>(`/api/tags/${id}`, { method: 'DELETE' }),
};

// === Inflow Routes ===
export const inflowRoutes = {
  list: () => request<{ routes: InflowRoute[] }>('/api/inflow-routes'),
  create: (body: {
    name: string;
    channel: string;
    description?: string;
    tag_ids?: number[];
  }) =>
    request<{ id: number; url_slug: string }>('/api/inflow-routes', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (
    id: number,
    body: Partial<{
      name: string;
      description: string | null;
      channel: string;
      is_active: boolean;
      tag_ids: number[];
    }>,
  ) =>
    request<{ updated: boolean }>(`/api/inflow-routes/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  remove: (id: number) =>
    request<void>(`/api/inflow-routes/${id}`, { method: 'DELETE' }),
};
